import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Mail, Linkedin, Play, Pause, Trash2, Sparkles, X, Send, Loader2, AlertTriangle } from "lucide-react";

const statusPill = (s) => {
  const m = {
    active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    draft: "bg-zinc-700/30 text-zinc-300 border-zinc-600/30",
    paused: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  };
  return m[s] || m.draft;
};

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", channel: "email", subject: "", message: "", steps: 3 });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchAll = useCallback(async () => {
    const { data } = await api.get("/campaigns");
    setItems(data);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError("");
    try {
      await api.post("/campaigns", form);
      setShowForm(false);
      setForm({ name: "", channel: "email", subject: "", message: "", steps: 3 });
      fetchAll();
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail) || "Could not create campaign. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (c) => {
    const next = c.status === "active" ? "paused" : "active";
    await api.patch(`/campaigns/${c.id}`, { status: next });
    fetchAll();
  };

  const remove = async (id) => {
    await api.delete(`/campaigns/${id}`);
    fetchAll();
  };

  const [sending, setSending] = useState(null);
  const [sendResult, setSendResult] = useState("");

  const sendCampaign = async (c) => {
    if (c.channel !== "email") {
      setSendResult("Only email campaigns can be sent right now.");
      setTimeout(() => setSendResult(""), 3500);
      return;
    }
    if (!window.confirm(
      `⚠️ WARNING: The leads in this workspace are AI-generated demo data. ` +
      `Their email addresses may not correspond to real people, and sending to fabricated addresses can hurt your sender reputation.\n\n` +
      `Only proceed if you have replaced the demo leads with real prospects.\n\n` +
      `Send "${c.name}" to up to 25 of your 'new' leads via Resend?`
    )) return;
    setSending(c.id);
    setSendResult("");
    try {
      const { data } = await api.post(`/campaigns/${c.id}/send`);
      setSendResult(`Sent ${data.sent} email${data.sent === 1 ? "" : "s"}${data.failed ? ` · ${data.failed} failed` : ""}`);
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail || "Send failed";
      setSendResult(typeof detail === "string" ? detail : "Send failed");
    } finally {
      setSending(null);
      setTimeout(() => setSendResult(""), 4500);
    }
  };

  const generateWithAI = () => {
    const templates = [
      { name: "Founders post-funding", channel: "email", subject: "A quick idea for {{company}}", message: "Hey {{first_name}},\n\nCongrats on the recent raise. Three things teams like {{company}} typically wrestle with in the first 90 days...", steps: 4 },
      { name: "RevOps in growth mode", channel: "linkedin", subject: "", message: "Hi {{first_name}}, noticed your team is scaling RevOps...", steps: 3 },
      { name: "CMOs in climate tech", channel: "email", subject: "{{company}} + a faster pipeline?", message: "Hi {{first_name}}, climate tech buyers are notoriously hard to source...", steps: 5 },
    ];
    const pick = templates[Math.floor(Math.random() * templates.length)];
    setForm(pick);
    setShowForm(true);
  };

  return (
    <div className="p-8 lg:p-12 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-eyebrow mb-2">Outreach</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Campaigns</h1>
          <p className="text-zinc-400 mt-2">AI-built multi-step sequences. Approve once, run forever.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={generateWithAI} className="btn-ghost text-sm" data-testid="generate-ai-campaign">
            <Sparkles className="w-4 h-4" /> Generate with AI
          </button>
          <button onClick={() => { setFormError(""); setShowForm(true); }} className="btn-primary text-sm" data-testid="new-campaign">
            <Plus className="w-4 h-4" /> New campaign
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {sendResult && (
          <div className="md:col-span-2 glass-card p-3 px-4 text-sm text-gold border border-yellow-300/30 flex items-center gap-2" data-testid="send-result">
            <Send className="w-4 h-4" /> {sendResult}
          </div>
        )}
        {items.length === 0 && (
          <div className="md:col-span-2 glass-card p-12 text-center text-zinc-500">
            No campaigns yet. Click <span className="text-gold">"New campaign"</span> to create your first sequence.
          </div>
        )}
        {items.map((c) => (
          <div key={c.id} className="glass-card glass-card-hover p-6" data-testid={`campaign-card-${c.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  {c.channel === "email" ? <Mail className="w-4 h-4 text-zinc-400" /> : <Linkedin className="w-4 h-4 text-zinc-400" />}
                  <span className="text-xs text-zinc-500 uppercase tracking-wider">{c.channel}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs border capitalize ${statusPill(c.status)}`}>{c.status}</span>
                </div>
                <h3 className="font-display text-xl text-white truncate">{c.name}</h3>
                <div className="text-xs text-zinc-500 mt-1">{c.steps} steps · {c.subject || "no subject (LinkedIn)"}</div>
              </div>
            </div>

            <p className="text-sm text-zinc-400 mb-5 line-clamp-2">{c.message}</p>

            <div className="grid grid-cols-3 gap-3 mb-5">
              <div>
                <div className="label-eyebrow mb-1">Sent</div>
                <div className="font-display text-xl text-white">{c.sent}</div>
              </div>
              <div>
                <div className="label-eyebrow mb-1">Opens</div>
                <div className="font-display text-xl text-white">{c.opens}</div>
              </div>
              <div>
                <div className="label-eyebrow mb-1">Replies</div>
                <div className="font-display text-xl text-gold">{c.replies}</div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-4 border-t border-white/5">
              <button onClick={() => toggle(c)} className="btn-ghost text-xs flex-1" data-testid={`toggle-${c.id}`}>
                {c.status === "active" ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Launch</>}
              </button>
              {c.channel === "email" && (
                <button onClick={() => sendCampaign(c)} disabled={sending === c.id} className="btn-ghost text-xs" data-testid={`send-${c.id}`}>
                  {sending === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5" /> Send</>}
                </button>
              )}
              <button onClick={() => remove(c.id)} className="px-3 py-2 rounded-full text-rose-300 hover:bg-rose-500/10 transition-colors" data-testid={`delete-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass-panel rounded-2xl p-8 w-full max-w-lg" data-testid="campaign-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl text-white">New campaign</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-eyebrow block mb-2">Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="input-glass" placeholder="Founders post-funding" data-testid="form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow block mb-2">Channel</label>
                  <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}
                          className="input-glass" data-testid="form-channel">
                    <option value="email">Email</option>
                    <option value="linkedin">LinkedIn</option>
                  </select>
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Steps</label>
                  <input type="number" min={1} max={9} value={form.steps}
                         onChange={(e) => setForm({ ...form, steps: parseInt(e.target.value || 1) })}
                         className="input-glass" data-testid="form-steps" />
                </div>
              </div>
              {form.channel === "email" && (
                <div>
                  <label className="label-eyebrow block mb-2">Subject line</label>
                  <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                         className="input-glass" placeholder="A quick idea for {{company}}" data-testid="form-subject" />
                </div>
              )}
              <div>
                <label className="label-eyebrow block mb-2">Opening message</label>
                <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                          className="input-glass resize-none" placeholder="Hey {{first_name}}..." data-testid="form-message" />
              </div>
            </div>
            {formError && (
              <div className="mt-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-300 flex items-start gap-2" data-testid="form-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {formError}
              </div>
            )}
            <div className="mt-6 flex items-center gap-3">
              <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="form-submit">
                {busy ? "Creating..." : "Create campaign"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
